import { useEffect, useState } from 'react';
import { resolveAssetUrl } from '@/shop/assetUrl';

export interface CharacterAnimationSource {
  id: string;
  name: string;
  idleUrl: string | null;
  winUrl: string | null;
  idleFrames: string[] | null;
  winFrames: string[] | null;
}

interface CharacterPreviewFrame {
  phase: 'IDLE' | 'WIN';
  phaseIndex: number;
  url: string;
}

function previewFrames(
  phase: CharacterPreviewFrame['phase'],
  urls: string[] | null,
  fallbackUrl: string | null,
): CharacterPreviewFrame[] {
  const source = urls?.length ? urls : fallbackUrl ? [fallbackUrl] : [];
  return source
    .filter((url, index, list) => !!url && list.indexOf(url) === index)
    .slice(0, 3)
    .map((url, index) => ({
      phase,
      phaseIndex: index + 1,
      url: resolveAssetUrl(url),
    }))
    .filter((frame): frame is CharacterPreviewFrame => !!frame.url);
}

export function CharacterAnimationPreview({
  character,
}: {
  character: CharacterAnimationSource;
}) {
  const frames = [
    ...previewFrames('IDLE', character.idleFrames, character.idleUrl),
    ...previewFrames('WIN', character.winFrames, character.winUrl),
  ];
  const [frameIndex, setFrameIndex] = useState(0);

  useEffect(() => {
    setFrameIndex(0);
    if (frames.length < 2) return;
    const timer = window.setInterval(() => {
      setFrameIndex((current) => (current + 1) % frames.length);
    }, 220);
    return () => window.clearInterval(timer);
  }, [character.id, frames.length]);

  if (frames.length === 0) {
    return (
      <div style={{ padding: 30, color: 'var(--ink-muted)', textAlign: 'center' }}>
        No character animation preview available.
      </div>
    );
  }

  const currentFrame = frames[frameIndex % frames.length];

  return (
    <>
      <div
        style={{
          minHeight: 320,
          position: 'relative',
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'center',
          overflow: 'hidden',
          borderRadius: 14,
          border: `1px solid ${
            currentFrame.phase === 'WIN'
              ? 'rgba(244,199,36,0.62)'
              : 'rgba(34,211,238,0.42)'
          }`,
          background:
            currentFrame.phase === 'WIN'
              ? 'radial-gradient(circle at 50% 62%, rgba(244,199,36,0.2), rgba(155,93,229,0.1) 42%, rgba(0,0,0,0.55) 72%)'
              : 'radial-gradient(circle at 50% 62%, rgba(34,211,238,0.16), rgba(0,0,0,0.55) 70%)',
          boxShadow:
            currentFrame.phase === 'WIN'
              ? 'inset 0 0 44px rgba(244,199,36,0.12)'
              : 'inset 0 0 36px rgba(34,211,238,0.08)',
          transition: 'border-color 160ms ease, background 160ms ease',
        }}
      >
        <img
          key={`${currentFrame.phase}-${currentFrame.phaseIndex}-${currentFrame.url}`}
          src={currentFrame.url}
          alt={`${character.name} ${currentFrame.phase.toLowerCase()} ${currentFrame.phaseIndex}`}
          draggable={false}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            animation: 'character-preview-frame 180ms ease-out both',
          }}
        />
        <span
          className="tag"
          style={{
            position: 'absolute',
            bottom: 10,
            left: '50%',
            transform: 'translateX(-50%)',
            color: currentFrame.phase === 'WIN' ? 'var(--c-yellow)' : 'var(--c-cyan)',
            fontSize: 10,
            padding: '3px 8px',
            borderRadius: 5,
            background: 'rgba(3,2,16,0.82)',
          }}
        >
          {currentFrame.phase} {currentFrame.phaseIndex}/3
        </span>
      </div>
      <div
        style={{
          marginTop: 10,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 5,
        }}
      >
        {frames.map((frame, index) => (
          <span
            key={`${frame.phase}-${frame.phaseIndex}-${frame.url}`}
            style={{
              width: index === frameIndex ? 18 : 6,
              height: 6,
              borderRadius: 999,
              background:
                index === frameIndex
                  ? frame.phase === 'WIN' ? 'var(--c-yellow)' : 'var(--c-cyan)'
                  : 'rgba(255,255,255,0.18)',
              transition: 'width 150ms ease, background 150ms ease',
            }}
          />
        ))}
      </div>
      <style>{`
        @keyframes character-preview-frame {
          from { opacity: 0.72; transform: scale(0.985); }
          to { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </>
  );
}
